import { computed, ref, onMounted, onUnmounted, inject } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'vuex';
import useAlert from '@/utils/Alert';
import useLoading from '@/utils/Loading';
import deepIsEqual from '@/utils/deepIsEqual';
import { POPUP_SETTINGS } from '@/models/POPUP_SETTINGS';
import { ALERT_TYPES } from '@/models/ALERT_TYPES';
import { DASHBOARD_OPTIONS } from '@/models/DASHBOARD_OPTIONS';
import { SE_SYNC_TYPES } from '@/models/SE_SYNC_TYPES';
import { fetchTableOfContentsSettings, saveTableOfContentsSettings } from '@/modules/table-of-contents';
import {
	nodesConvertToArticles,
	reduceArticles,
	deleteObjectById,
	provideTitleProperty,
	removeTitleProperty,
	flattenExceededDepth,
	flattenStructure,
} from '@/modules/table-of-contents';
import type { Article, TableOfContentsSettingsType } from '@/modules/table-of-contents';

interface DragEndEvent {
	from: HTMLElement;
	to: HTMLElement;
	newIndex: number;
	item: { _underlying_vm_: Article };
}

export function useTableOfContentsSettings() {
	const { t } = useI18n();
	const store = useStore();
	const { alert, alertHide, alertShow, alertCreate } = useAlert();
	const { loading, loadingShow, loadingHide } = useLoading();
	const emitter = inject('emitter') as {
		on: (event: string, handler: (...args: unknown[]) => void) => void;
		off: (event: string, handler: (...args: unknown[]) => void) => void;
	};

	const panelOpenedKey = ref<number | null>(null);
	const language = computed(() => store.getters['maps/languages/getLanguageComputed']);
	const nodesOriginal = computed(() => store.getters['nodes/getRawNodes']);

	// Articles
	const articles = ref<Article[]>([]);
	const articlesOrdered = ref<Article[]>([]);
	const articlesOrderedInitial = ref<Article[]>([]);
	const isArticlesChanged = computed(() => !deepIsEqual(articlesOrdered.value, articlesOrderedInitial.value));

	// Settings
	const settings = ref<TableOfContentsSettingsType | null>(null);
	const settingsInitial = ref<TableOfContentsSettingsType | null>(null);

	// URLs
	const mapsURL = computed<string>(() => store.getters['maps/getMapsURL']);
	const mapId = computed<number>(() => store.getters['maps/getCurrentMapId']);
	const tocURL = computed<string>(() => store.getters['maps/getTOCURL']);

	// Save availability
	const isSaveAvailable = computed(() => !deepIsEqual(settings.value, settingsInitial.value));
	const isSaveAvailableClassname = computed(() =>
		!isSaveAvailable.value && !isArticlesChanged.value ? 'disabled' : '',
	);

	function parseNestedArticles(event: DragEndEvent): void {
		const fromOrderedList = event.from.classList.contains('--sort');
		const toOrderedList = event.to.classList.contains('--sort');

		if (fromOrderedList && !toOrderedList) {
			const index = event.newIndex;
			const item = articles.value[index];
			const children = item.children;

			if (children.length) {
				const flatItems = flattenStructure([item]);
				articles.value.splice(index, 1, ...flatItems);
			}
		}

		if (fromOrderedList && toOrderedList) {
			const item = event.item._underlying_vm_;
			const children = item.children;
			if (children.length) {
				flattenExceededDepth(articlesOrdered.value, 4);
			}
		}
	}

	function settingsOpen(link: string): void {
		store.dispatch('settingsPopup/popupOpen', link);
	}

	// Simultaneous editing
	function removeArticle(id: number): void {
		const unsortedIndex = articles.value.findIndex((item) => item.id === id);
		if (unsortedIndex !== -1) {
			articles.value.splice(unsortedIndex, 1);
			return;
		}
		deleteObjectById(articlesOrdered.value, id);
		updateArticles();
	}

	function updateArticles(): void {
		const articlesTmp = nodesConvertToArticles(nodesOriginal.value, language.value.id);
		articles.value = reduceArticles(articlesTmp, articlesOrdered.value);
	}

	async function settingsSave(): Promise<void> {
		if (!isSaveAvailable.value && !isArticlesChanged.value) return;
		loadingShow();
		try {
			const payload = {
				content: {
					show: settings.value!.content.show,
					individual: settings.value!.content.individual,
					individual_order: removeTitleProperty(articlesOrdered.value),
				},
				glossary: {
					show: settings.value!.glossary.show,
				},
				sources: {
					show: settings.value!.sources.show,
				},
			};
			await saveTableOfContentsSettings(mapsURL.value, mapId.value, tocURL.value, payload);
			settingsInitial.value = JSON.parse(JSON.stringify(settings.value));
			articlesOrderedInitial.value = JSON.parse(JSON.stringify(articlesOrdered.value));
			store.dispatch('tocWarnings/checkDeletedNodes');
			store.dispatch('simultaneousEditing/syncEdits', { type: SE_SYNC_TYPES.TOC_SAVED, data: null });
			alertCreate({ type: ALERT_TYPES.SUCCESS, message: t('popup.url.message.success') });
			alertShow();
		} catch {
			alertCreate({ type: ALERT_TYPES.ERROR, message: t('popup.url.message.errorSave') });
			alertShow();
		} finally {
			loadingHide();
		}
	}

	async function settingsFetch(): Promise<void> {
		loadingShow();
		try {
			const { data: settingsData } = await fetchTableOfContentsSettings(
				mapsURL.value,
				mapId.value,
				tocURL.value,
			);
			const articlesTmp = nodesConvertToArticles(nodesOriginal.value, language.value.id);
			articlesOrdered.value = provideTitleProperty(
				settingsData.content.individual_order,
				nodesOriginal.value,
				language.value.id,
			);
			articlesOrderedInitial.value = JSON.parse(JSON.stringify(articlesOrdered.value));
			articles.value = reduceArticles(articlesTmp, articlesOrdered.value);

			const hasDeletedArticles = articlesOrdered.value.some(function checkDeleted(a: Article): boolean {
				return a.deleted === true || (a.children && a.children.some(checkDeleted));
			});
			if (hasDeletedArticles) {
				alertCreate({ type: ALERT_TYPES.WARNING, message: t('popup.confirm.tocDeletedNodesAlert') });
				alertShow();
			}

			settingsData.glossary.link = POPUP_SETTINGS.CONTENT.SECTIONS['0'].GLOSSARY.VALUE;
			settingsData.sources.link = POPUP_SETTINGS.CONTENT.SECTIONS['0'].LIST_REFERENCES.VALUE;
			settings.value = JSON.parse(JSON.stringify(settingsData));
			settingsInitial.value = JSON.parse(JSON.stringify(settingsData));
		} catch {
			alertCreate({ type: ALERT_TYPES.ERROR, message: t('popup.header.message.errorLoading') });
			alertShow();
		} finally {
			loadingHide();
		}
	}

	// Lifecycle — simultaneous editing lock & emitter events
	const BLOCKED_TYPE = DASHBOARD_OPTIONS.ORGANIZE.GROUPS['0'].TABLE_OF_CONTENT.BLOCKED_TYPE;

	onMounted(() => {
		store.dispatch('simultaneousEditing/startEditingOption', BLOCKED_TYPE);
		emitter.on('removedNodeTOC', removeArticle as (...args: unknown[]) => void);
		emitter.on('addedNodeTOC', updateArticles as (...args: unknown[]) => void);
	});

	onUnmounted(() => {
		store.dispatch('simultaneousEditing/stopEditingOption', BLOCKED_TYPE);
		emitter.off('removedNodeTOC', removeArticle as (...args: unknown[]) => void);
		emitter.off('addedNodeTOC', updateArticles as (...args: unknown[]) => void);
	});

	// Fetch on init
	settingsFetch();

	return {
		t,
		loading,
		alert,
		alertHide,
		panelOpenedKey,
		settings,
		settingsOpen,
		isSaveAvailableClassname,
		settingsSave,
		articles,
		articlesOrdered,
		parseNestedArticles,
	};
}
